import { useEffect, useState } from 'react';
import { payableForFace } from './rewardDiscount';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Pricing a single gift-card line against the server.
 *
 * `POST /giftcards/quote` runs the same code path as `/purchase/bulk`, so a row
 * it returns is what will be charged — not a second implementation that happens
 * to agree. Shared because the product page and the listing's quick-add both
 * need it, and money logic in two places is how the two start disagreeing.
 *
 * @returns the quote's own row: `{face_value, discount_type, discount_value,
 *          discount_amount, payable_amount, available, reason, max_quantity, …}`
 */
export async function quoteLine({ rewardId, denominationPaise, quantity = 1 }) {
  if (!rewardId || !denominationPaise) return null;
  const token = localStorage.getItem('token');

  try {
    const res = await fetch(`${API}/giftcards/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({
        items: [{
          reward_id: String(rewardId).replace(/^reward_/, ''),
          denomination: denominationPaise,
          quantity,
        }],
      }),
    });
    if (!res.ok) return null;
    return (await res.json())?.items?.[0] || null;
  } catch {
    return null;
  }
}

/**
 * A face value the customer typed, priced by the server.
 *
 * The quote takes `denomination` as the *payable* amount and offers no
 * face-based lookup — confirmed against production, where sending ₹500 as a
 * denomination prices a ₹510.20 card. So the face is converted to a payable
 * only to form the request; every figure the caller renders comes back from the
 * response, and the amount handed to the cart is the server's rather than the
 * one we guessed with.
 *
 * Debounced, because this runs per keystroke, and sequenced so a slow reply
 * cannot overwrite a newer one.
 *
 * @returns {{row, pricing}} — `row` is null until a valid value is priced.
 */
export function useFaceQuote({ rewardId, range, face, quantity = 1 }) {
  const [row, setRow] = useState(null);
  const [pricing, setPricing] = useState(false);

  /*
    Depend on the range's numbers, never on the object.

    `getRewardRange` builds a fresh object on every render, so passing it
    straight into the dependency array re-ran this effect every render: each run
    set `pricing` true and the next render's cleanup cancelled its timer before
    it could fire. The request never completed, "You pay" sat on "—" for ever
    and Add to Cart stayed disabled — with the value and saving beside it filled
    in from an earlier run, which is what made it look like a display bug rather
    than a loop.
  */
  const minFace = range?.minFace;
  const maxFace = range?.maxFace;
  const discountType = range?.discountType;
  const discountValue = range?.discountValue;

  useEffect(() => {
    const value = Number(face);
    const bounds = minFace != null && maxFace != null
      ? { minFace, maxFace, discountType, discountValue }
      : null;

    if (!rewardId || !bounds || !Number.isFinite(value)
      || value < bounds.minFace || value > bounds.maxFace) {
      // Out of bounds or empty: drop the figures rather than leaving the last
      // valid price sitting under a value it no longer belongs to.
      setRow(null);
      setPricing(false);
      return undefined;
    }

    let cancelled = false;
    setPricing(true);

    const timer = setTimeout(() => {
      quoteLine({
        rewardId,
        denominationPaise: Math.round(payableForFace(value, bounds) * 100),
        quantity,
      })
        .then((result) => { if (!cancelled) setRow(result); })
        .finally(() => { if (!cancelled) setPricing(false); });
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [rewardId, minFace, maxFace, discountType, discountValue, face, quantity]);

  return { row, pricing };
}
